import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, PRIMARY_OUTLET, Router } from '@angular/router';
import { config } from '@app/core/app-config';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { DefaultRuleSet, LocalStorageKey, SignupUser, SupportPreference } from '@app/shared/interfaces';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { Rb2bService } from '@app/shared/services/rb2b.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { SettingsService } from '@app/shared/services/settings/settings.service';
import { decodeToRealUserId } from '@app/shared/utils';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SignupChannel, SupportedChannels } from './signup.interface';

export interface CsTime {
  timezone: string; // UTC
  start_time: number; // Start hour
  end_time: number; // End hour
}

const FLOW = [
  'about',
  'welcome',
  'validate-phone',
  'start',
  'properties',
  'channels',
  'password',
  'trial',
  'messaging-rules',
  'done',
];

const URL_ROOT = '/user/register/'; // don't like this but relativeTo: doesn't seem to do the trick when navigating
const API_ROOT = `${config.API_URL}/signup`;

@Injectable({
  providedIn: 'root',
})
export class SignupWizardService {
  emailPlaceholder = config.EMAIL_PLACEHOLDER;

  constructor(
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly authService: AuthenticationService,
    private readonly segmentIoService: SegmentIoService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly deviceDetectionService: DeviceDetectionService,
    private readonly settingsService: SettingsService,
    private readonly rb2bService: Rb2bService
  ) {
    this.refreshUserFromLocalStorage();

    // Load rb2b tracking for sales
    this.rb2bService.init();
  }

  public signupUser: SignupUser = {
    status: 'new',
    current_step: 1,
    email: '',
  };

  // Used to store IP-specific timezone data for the Support start/end times for a user
  public csTime: CsTime;

  isAirbnbSignup(): boolean {
    return localStorage.getItem(LocalStorageKey.isAirbnbSignUp) ? true : false;
  }

  getAirbnbChannelUserId(): string {
    return localStorage.getItem(LocalStorageKey.signUpAirbnbChannelUserId);
  }

  setTokenHeader() {
    const token = this.signupUser.token;

    if (token) {
      let headers = new HttpHeaders();
      headers = headers.set('Authorization', `Bearer ${token}`);

      return headers;
    } else {
      return null;
    }
  }

  startOver() {
    this.authService.logout(false);

    this.signupUser = {
      status: 'new',
      current_step: 1,
      email: '',
    };

    this.activatedRoute.queryParams.subscribe((params) => {
      this.signupUser.email = params['email'];
    });
  }

  forgetUserAndStartOver(email: string): Observable<SignupUser> {
    // calls the API to forget what we know about the user
    return this.http.post(`${API_ROOT}/start-over`, { email }).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);
        return res.data;
      })
    );
  }

  goBackToWhereILeftOff(email: string): Observable<SignupUser | { error: boolean; message: string }> {
    return this.http.post(`${API_ROOT}/login-returning-user`, { email }).pipe(
      map((res: { data: SignupUser }) => {
        return res.data;
      }),
      catchError((err) => {
        return of({ error: true, message: '' });
      })
    );
  }

  public validateEmail(email: string): Observable<SignupUser | { error: boolean; message: string }> {
    return this.http.get(`${API_ROOT}/email/${email}`).pipe(
      map((res: { data: SignupUser }) => {
        this.signupUser = res.data;

        // Report to segment if we're not on the happy path
        if (this.signupUser.status !== 'new') {
          this.segmentIoService.track(SegmentEvent.SEGMENT_ERROR_KEY, {
            status: this.signupUser.status,
            message: '',
          });
        }

        return res.data;
      }),
      catchError((err) => {
        if (err.error?.errors?.email[0]) {
          // Segment track
          this.segmentIoService.track(SegmentEvent.SEGMENT_ERROR_KEY, {
            status: 'error',
            message: err.error?.errors?.email[0],
          });

          return of({
            error: true,
            message: err.error?.errors?.email[0],
          });
        } else {
          // Segment track
          this.segmentIoService.track(SegmentEvent.SEGMENT_ERROR_KEY, {
            status: 'error',
            message: 'We were unable to complete your signup at this time',
          });

          return of({
            error: true,
            message: 'We were unable to complete your signup at this time',
          });
        }
      })
    );
  }

  public continueReturningUser(token: string): Observable<SignupUser | { error: boolean; message: string }> {
    return this.http.get(`${API_ROOT}/token/${token}`).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);

        // Identify with Segment
        if (res.data.id) {
          this.identifyWithSegment(res.data);
        }

        return res.data;
      }),
      catchError((err: HttpErrorResponse) => {
        return of({
          error: true,
          message: `We're having some trouble continuing your application. We've logged this, please reach out to support if this keeps happening.`,
        });
      })
    );
  }

  public createSignupUser(
    email: string,
    first_name: string,
    last_name: string,
    mobile_phone: string,
    agreed_to_tos: boolean,
    captcha: string,
    partnerDiscountCode?: string
  ): Observable<SignupUser | { error: boolean; message: string }> {
    const payload = {
      email,
      first_name,
      last_name,
      mobile_phone,
      agreed_to_tos,
      captcha,
      code: partnerDiscountCode,
    };

    const referrerId = (window as any).growsurf?.getReferrerId();

    if (referrerId) {
      payload['referral_id'] = referrerId;
    }

    const headers = this.setTokenHeader();

    return this.http.post(`${API_ROOT}/steps/user-details`, payload, { headers }).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);

        // Identify with Segment
        if (res.data.id) {
          this.identifyWithSegment(res.data);
        }

        return res.data;
      }),
      catchError((err) => {
        return of({
          error: true,
          message: err.error?.message ?? 'We were unable to complete your signup at this time',
        });
      })
    );
  }

  private identifyWithSegment(user: SignupUser): void {
    const user_id = decodeToRealUserId(user.id, user.email) ?? '0';

    this.segmentIoService.identify(user_id, {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.mobile_phone,
      picture: user.avatar,
    });
  }

  public updatePropertyCount(properties: number): Observable<SignupUser> {
    const payload = { property_count: properties };
    const headers = this.setTokenHeader();

    return this.http.post(`${API_ROOT}/steps/properties`, payload, { headers }).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);
        return res.data;
      })
    );
  }

  public updatePlatforms(platforms: string[]): Observable<SignupUser> {
    const payload = { platforms };
    const headers = this.setTokenHeader();

    return this.http.post(`${API_ROOT}/steps/platforms`, payload, { headers }).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);
        return res.data;
      })
    );
  }

  public updatePassword(
    password: string,
    password_confirmation: string
  ): Observable<SignupUser | { error: boolean; message: string }> {
    const headers = this.setTokenHeader();
    return this.http.post(`${API_ROOT}/steps/password`, { password, password_confirmation }, { headers }).pipe(
      map((res: { data: SignupUser; meta?: { cs_time: CsTime } }) => {
        if (res.meta?.cs_time) {
          this.csTime = res.meta.cs_time;
        }
        this.mergeUserWithExistingData(res.data);
        return res.data;
      }),
      catchError((err) => {
        return of({ error: true, message: '' });
      })
    );
  }

  public updateOnboarding(onboarding_type: string): Observable<SignupUser> {
    const headers = this.setTokenHeader();

    const payload = { onboarding_type };

    return this.http.post(`${API_ROOT}/steps/start-trial`, payload, { headers }).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);
        return res.data;
      })
    );
  }

  public updateRulesets(rulesets: DefaultRuleSet[]): Observable<SignupUser> {
    const headers = this.setTokenHeader();

    return this.http.post(`${API_ROOT}/steps/messaging-rulesets`, { rulesets }, { headers }).pipe(
      map((res: { data: SignupUser }) => {
        this.mergeUserWithExistingData(res.data);
        return res.data;
      })
    );
  }

  public updateSupportPreference(supportPreference: SupportPreference): Observable<SignupUser> {
    const headers = this.setTokenHeader();

    return this.http
      .post(`${API_ROOT}/steps/support-preference`, { support_preference: supportPreference }, { headers })
      .pipe(
        map((res: { data: SignupUser }) => {
          this.mergeUserWithExistingData(res.data);
          return res.data;
        })
      );
  }

  public flowCompleted(hasAirbnb: boolean, redirectUrl: string = config.ONBOARDING_PAGE) {
    const token = this.signupUser.token;

    if (!token) {
      return;
    }

    const isAirbnbSignUp = localStorage.getItem(LocalStorageKey.isAirbnbSignUp);
    localStorage.removeItem(LocalStorageKey.signUpUser);
    localStorage.removeItem(LocalStorageKey.isAirbnbSignUp);
    localStorage.removeItem(LocalStorageKey.signUpAirbnbChannelUserId);
    localStorage.removeItem(LocalStorageKey.signUpAirbnbState);
    localStorage.setItem('accessToken', token);

    this.authService.isLoggedInSubject.next(true);

    // localStorage set is not instant and has no callback
    setTimeout(() => {
      this.authService.getUserDetailsFromApi().subscribe((res) => {
        if (res.error) {
          this.segmentIoService.track(SegmentEvent.SignupUserApiFailedToLoad, {});
        } else {
          localStorage.setItem('user', JSON.stringify(res));

          // Send the registration event to Segment
          this.segmentIoService.track(SegmentEvent.AccountCreated, {
            label: 'Trial',
            isAirbnbSignUp,
          });

          this.signupUser = {
            status: 'new',
            current_step: 1,
            email: '',
          };

          if (hasAirbnb) {
            redirectUrl = `/accounts/new/airbnb`;
            redirectUrl = isAirbnbSignUp ? `/onboarding` : redirectUrl;
            this.authService.loginActions(redirectUrl);
          } else {
            this.authService.loginActions(redirectUrl);
          }
        }
      });
    }, 100);
  }

  public goToStep(step: number): Observable<SignupUser> {
    const headers = this.setTokenHeader();

    return this.http.post(`${API_ROOT}/goto/${step}`, {}, { headers }).pipe(
      map((res: { data: SignupUser; meta?: { cs_time: CsTime } }) => {
        if (res.meta?.cs_time) {
          this.csTime = res.meta.cs_time;
        }
        this.mergeUserWithExistingData(res.data);
        return res.data;
      })
    );
  }

  private mergeUserWithExistingData(newUser: SignupUser) {
    this.signupUser = { ...this.signupUser, ...newUser };
  }

  getCurrentStep() {
    const path = this.router.parseUrl(this.router.url).root.children[PRIMARY_OUTLET].segments.pop().path;

    const number = FLOW.indexOf(path);
    return {
      path,
      number,
      next: number < FLOW.length - 1 ? FLOW[number + 1] : null,
      prev: number > 0 ? FLOW[number - 1] : null,
    };
  }

  hasNext(): boolean {
    return !!this.getCurrentStep().next;
  }

  hasPrevious(): boolean {
    return !!this.getCurrentStep().prev;
  }

  next() {
    this.router.navigateByUrl(URL_ROOT + this.getCurrentStep().next);
  }

  previous() {
    const currentStep = this.signupUser?.current_step;

    const newStep = currentStep - 1;

    if (newStep >= 2) {
      this.goToStep(newStep).subscribe((res) => {
        this.router.navigateByUrl(URL_ROOT + this.getCurrentStep().prev);
      });
    } else {
      this.router.navigateByUrl(URL_ROOT + this.getCurrentStep().prev);
    }
  }

  cancel() {
    this.router.navigateByUrl('/user/register/welcome');
  }

  marketing() {
    // iOS needs to remain in the app at all times
    if (this.deviceDetectionService.isIosApp()) {
      this.router.navigate(['/user/hello']);
    } else {
      window.location.href = 'https://hospitable.com';
    }
  }

  getSupportedChannelCount(): number {
    if (!this.signupUser) {
      return;
    }

    if (this.signupUser.platforms) {
      return this.signupUser.platforms.filter((channel: SignupChannel) => SupportedChannels.indexOf(channel) >= 0)
        .length;
    }
  }

  public stashUserInLocalStorage() {
    localStorage.setItem(LocalStorageKey.signUpUser, JSON.stringify(this.signupUser));
  }

  public refreshUserFromLocalStorage() {
    if (localStorage.getItem(LocalStorageKey.signUpUser)) {
      try {
        this.signupUser = JSON.parse(localStorage.getItem(LocalStorageKey.signUpUser));
        localStorage.removeItem(LocalStorageKey.signUpUser);

        // head to the right step based on the signup user
        if (this.signupUser.current_step > 2) {
          this.router.navigateByUrl(URL_ROOT + 'start');
        }
      } catch {
        console.log('Error parsing localstorage Signup User');
      }
    }
  }

  validatePhoneWithOtp(code: string): Observable<boolean> {
    const headers = this.setTokenHeader();
    return this.http.post(`${API_ROOT}/phone/verify`, { code }, { headers }).pipe(
      map(() => {
        this.signupUser.current_step = 3;
        return true;
      }),
      catchError(() => of(false))
    );
  }

  requestNewPhoneOtp(): Observable<boolean> {
    const headers = this.setTokenHeader();

    return this.http.post(`${API_ROOT}/phone/send`, {}, { headers }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
